import { useState, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useMotionValue } from 'framer-motion';
import { useStyletron } from '@olxeu-jobs/ui';
import { Platform } from '~types';
import { FullLazyMotion } from '../motion';
import {
  CarouselWrapper,
  CarouselItemWrapper,
  MotionCarouselItemsWrapper,
} from './styled-components';
import { Dots } from './dots';

const DRAG_BUFFER = 50;
const SPRING_OPTIONS = {
  type: 'spring',
  mass: 2,
  stiffness: 400,
  damping: 50,
};

export type CarouselHandle = {
  nextSlide: () => void;
  prevSlide: () => void;
  slideCount: number;
};

export type CarouselItem = {
  key: React.Key;
  node: React.ReactNode;
};

export type CarouselSlideSource = 'dot' | 'control' | 'drag';

type Props = {
  items: CarouselItem[];
  itemWidthPercent?: number;
  hideDots?: boolean;
  dotsActivated?: boolean;
  onSlideChange?: ({
    currentIndex,
    prevIndex,
    slideSource,
  }: {
    currentIndex: number;
    prevIndex: number;
    slideSource: CarouselSlideSource;
  }) => void;
};

export const Carousel = forwardRef<CarouselHandle, Props>(
  (
    { items, itemWidthPercent = 100, hideDots, onSlideChange, dotsActivated = true }: Props,
    ref,
  ) => {
    const [index, setIndex] = useState(0);
    const constraintsRef = useRef(null);
    const dragX = useMotionValue(0);
    const [, theme] = useStyletron();
    const isMobile = theme.platform === Platform.MOBILE;
    const itemsInView = Math.floor(100 / itemWidthPercent);
    const slideCount = Math.ceil(items.length / itemsInView);

    const handleSlideChange = useCallback(
      ({
        relative,
        value,
        source,
      }: {
        relative: boolean;
        value: number;
        source: CarouselSlideSource;
      }) => {
        let currentIndex;

        if (relative) {
          currentIndex = Math.min(Math.max(index + value, 0), slideCount - 1);
        } else {
          currentIndex = Math.min(Math.max(value, 0), slideCount - 1);
        }

        setIndex(currentIndex);
        onSlideChange?.({
          currentIndex,
          prevIndex: index,
          slideSource: source,
        });
      },
      [slideCount, onSlideChange, index],
    );

    const onDragEnd = () => {
      const x = dragX.get();

      if (x <= -DRAG_BUFFER && index < items.length - 1) {
        handleSlideChange({ relative: true, value: 1, source: 'drag' });
      } else if (x >= DRAG_BUFFER && index > 0) {
        handleSlideChange({ relative: true, value: -1, source: 'drag' });
      }
    };

    const getItemVisibility = (itemIndex: number) => {
      const visibleStartIndex = index * itemsInView;
      const endIndexAdjustment = isMobile ? 1 : 0;
      const visibleEndIndex = Math.min(
        visibleStartIndex + itemsInView + endIndexAdjustment,
        items.length,
      );

      return itemIndex >= visibleStartIndex && itemIndex < visibleEndIndex;
    };

    useImperativeHandle(
      ref,
      (): CarouselHandle => ({
        nextSlide: () => {
          handleSlideChange({ relative: true, value: 1, source: 'control' });
        },
        prevSlide: () => {
          handleSlideChange({ relative: true, value: -1, source: 'control' });
        },
        slideCount,
      }),
      [slideCount, handleSlideChange],
    );

    return (
      <FullLazyMotion>
        <CarouselWrapper ref={constraintsRef}>
          <MotionCarouselItemsWrapper
            data-testid="motion-carousel-items-wrapper"
            dragListener={isMobile}
            drag="x"
            dragConstraints={constraintsRef}
            style={{
              x: dragX,
            }}
            animate={{
              translateX: `-${index * itemsInView * itemWidthPercent}%`,
            }}
            transition={SPRING_OPTIONS}
            onDragEnd={onDragEnd}
          >
            {items?.map(({ key, node }, itemIndex) => (
              <CarouselItemWrapper
                key={key}
                $itemWidthPercent={itemWidthPercent}
                aria-hidden={!getItemVisibility(itemIndex)}
                {...{ inert: !getItemVisibility(itemIndex) ? '' : undefined }}
              >
                {node}
              </CarouselItemWrapper>
            ))}
          </MotionCarouselItemsWrapper>
          {!hideDots && slideCount > 1 && (
            <Dots
              count={slideCount}
              activeIndex={index}
              onDotClick={(i) => {
                handleSlideChange({ relative: false, value: i, source: 'dot' });
              }}
              dotsActivated={dotsActivated}
            />
          )}
        </CarouselWrapper>
      </FullLazyMotion>
    );
  },
);
